import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, TextField, Box, Grid, IconButton, Typography, 
    Stack, Select, MenuItem, FormControl, InputLabel, 
    Divider, Paper
} from '@mui/material';
import { MdFamilyRestroom, MdDelete, MdClose, MdAdd } from "react-icons/md";
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../App.js';
import ConfirmModal from '../../../components/ConfirmModal';

const MemberModal = ({ show, closeModal, setPlayers, editMode = false, playerToEdit = null }) => {
    const [memberData, setMemberData] = useState({
        firstName: '', lastName: '', email: '', role: 0,
        bookingTimes: 2, familyMembers: [], isActive: true, exitDate: ''
    });

    const [confirmConfig, setConfirmConfig] = useState({
        open: false, title: '', message: '', onConfirm: () => {}, type: 'question'
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show) {
            if (editMode && playerToEdit) {
                setMemberData({
                    firstName: playerToEdit.firstName || '',
                    lastName: playerToEdit.lastName || '',
                    email: playerToEdit.email || '',
                    role: playerToEdit.role || 0,
                    bookingTimes: playerToEdit.bookingTimes ?? 2,
                    familyMembers: playerToEdit.familyMembers || [],
                    isActive: playerToEdit.isActive ?? true,
                    exitDate: playerToEdit.exitDate ? playerToEdit.exitDate.split('T')[0] : ''
                });
            } else {
                setMemberData({
                    firstName: '', lastName: '', email: '', role: 0,
                    bookingTimes: 2, familyMembers: [], isActive: true, exitDate: ''
                });
            }
        }
    }, [editMode, playerToEdit, show]);

    const handleInputChange = (e, field) => {
        let value = e.target.value;
        if (field === 'bookingTimes') {
            const numericValue = parseInt(value, 10);
            value = (isNaN(numericValue) || numericValue < 0) ? "" : numericValue;
        }
        setMemberData({ ...memberData, [field]: value });
    };

    const handleSave = async () => {
        if (memberData.familyMembers.some(fm => !fm.firstName?.trim() || !fm.lastName?.trim())) {
            return toast.error("Bitte fülle Vor- und Nachname für alle Familienmitglieder aus (oder lösche leere Einträge).");
        }

        setLoading(true);
        try {
            const dataToProcess = { ...memberData };
            if (dataToProcess.exitDate === '') dataToProcess.exitDate = null;

            const sanitizedData = Object.fromEntries(
                Object.entries(dataToProcess).filter(([key, value]) => {
                    if (key === 'familyMembers') return value.length > 0;
                    return value !== '' || value === null; 
                })
            );

            if (editMode && playerToEdit) {
                await axios.put(`${API_BASE_URL}/api/admin/updatePlayer/${playerToEdit._id}`, sanitizedData);
                toast.success('Spieler erfolgreich aktualisiert');
            } else {
                const response = await axios.post(`${API_BASE_URL}/api/createPlayer`, sanitizedData);
                if (response.data.message) {
                    if (response.data.message.includes('E-Mail konnte nicht gesendet werden')) {
                        toast.warning(response.data.message);
                    } else if (response.data.message.includes('E-Mail-Adresse existiert bereits')) {
                        toast.error(response.data.message);
                    } else {
                        toast.success(response.data.message);
                    }
                }
            }

            const updatedPlayers = await axios.get(`${API_BASE_URL}/api/allplayersadmin`);
            setPlayers(updatedPlayers.data);
            closeModal();
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler beim Speichern");
        } finally {
            setLoading(false);
        }
    };

    const addFamilyMember = () => {
        setMemberData({
            ...memberData,
            familyMembers: [...memberData.familyMembers, { firstName: '', lastName: '', bookingTimes: 2 }]
        });
    };

    const removeFamilyMember = (index) => {
        setConfirmConfig({
            open: true,
            title: 'Familienmitglied löschen',
            message: 'Möchtest du dieses Familienmitglied wirklich löschen?',
            onConfirm: () => {
                setMemberData({
                    ...memberData,
                    familyMembers: memberData.familyMembers.filter((_, i) => i !== index)
                });
                setConfirmConfig(prev => ({ ...prev, open: false }));
            },
            type: 'delete'
        });
    };

    const handleFamilyMemberChange = (index, field, value) => {
        const updatedFamilyMembers = [...memberData.familyMembers];
        updatedFamilyMembers[index][field] = value;
        setMemberData({ ...memberData, familyMembers: updatedFamilyMembers });
    };

    const handleDelete = () => {
        setConfirmConfig({
            open: true,
            title: 'Spieler löschen',
            message: 'Möchtest du diesen Spieler wirklich löschen?',
            onConfirm: executeDelete,
            type: 'delete'
        });
    };

    const executeDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/api/players/${playerToEdit._id}`);
            toast.success("Spieler erfolgreich gelöscht");
            const updatedPlayers = await axios.get(`${API_BASE_URL}/api/allplayersadmin`);
            setPlayers(updatedPlayers.data);
            closeModal();
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler beim Löschen");
        } finally {
            setConfirmConfig(prev => ({ ...prev, open: false }));
        }
    };

    return (
        <Dialog open={show} onClose={closeModal} fullWidth maxWidth="sm">
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', color: 'white', px: 4, py: 2,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <Typography variant="h6" component="div" fontWeight="bold">
                    {editMode ? "Mitglied bearbeiten" : "Mitglied anlegen"}
                </Typography>
                <IconButton onClick={closeModal} sx={{ color: 'white' }} size="small">
                    <MdClose />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 4, py: 3 }}>
                <Stack spacing={3} sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                            label="Vorname"
                            fullWidth
                            size="small"
                            value={memberData.firstName}
                            onChange={(e) => handleInputChange(e, 'firstName')}
                        />
                        <TextField
                            label="Nachname"
                            fullWidth
                            size="small"
                            value={memberData.lastName}
                            onChange={(e) => handleInputChange(e, 'lastName')}
                        />
                    </Box>

                    <TextField
                        label="E-Mail"
                        fullWidth
                        size="small"
                        value={memberData.email}
                        onChange={(e) => handleInputChange(e, 'email')}
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Rolle</InputLabel>
                            <Select
                                value={memberData.role}
                                label="Rolle"
                                onChange={(e) => handleInputChange(e, 'role')}
                            >
                                <MenuItem value={0}>Mitglied</MenuItem>
                                <MenuItem value={1}>Trainer</MenuItem>
                                <MenuItem value={2}>Admin</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Gleichzeitige Buchungen"
                            type="number"
                            fullWidth
                            size="small"
                            value={memberData.bookingTimes}
                            onChange={(e) => handleInputChange(e, 'bookingTimes')}
                        />
                    </Box>

                    {editMode && (
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={memberData.isActive}
                                    label="Status"
                                    onChange={(e) => handleInputChange(e, 'isActive')}
                                >
                                    <MenuItem value={true}>Aktiv</MenuItem>
                                    <MenuItem value={false}>Inaktiv</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Austrittsdatum"
                                type="date"
                                fullWidth
                                size="small"
                                value={memberData.exitDate}
                                onChange={(e) => handleInputChange(e, 'exitDate')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MdFamilyRestroom color="#1976d2" size={20} /> Familienmitglieder
                            </Typography>
                            <Button 
                                size="small" 
                                startIcon={<MdAdd />} 
                                onClick={addFamilyMember}
                                sx={{ 
                                    minWidth: { xs: 'auto', sm: '64px' },
                                    '& .MuiButton-startIcon': { 
                                        margin: { xs: 0, sm: '0 8px 0 -4px' } 
                                    }
                                }}
                            >
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                    Hinzufügen
                                </Box>
                            </Button>
                        </Box>

                        {memberData.familyMembers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                Keine Familienmitglieder hinterlegt
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {memberData.familyMembers.map((fm, idx) => (
                                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, position: 'relative', bgcolor: '#fafafa' }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Vorname"
                                                    fullWidth
                                                    size="small"
                                                    value={fm.firstName}
                                                    onChange={(e) => handleFamilyMemberChange(idx, 'firstName', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Nachname"
                                                    fullWidth
                                                    size="small"
                                                    value={fm.lastName}
                                                    onChange={(e) => handleFamilyMemberChange(idx, 'lastName', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={10} sm={3}>
                                                <TextField
                                                    label="Buchungen"
                                                    type="number"
                                                    fullWidth
                                                    size="small"
                                                    value={fm.bookingTimes !== undefined ? fm.bookingTimes : 2}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value, 10);
                                                        handleFamilyMemberChange(idx, 'bookingTimes', (isNaN(val) || val < 0) ? '' : val);
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={2} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <IconButton color="error" size="small" onClick={() => removeFamilyMember(idx)}>
                                                    <MdDelete />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ 
                px: 4, py: 2, bgcolor: '#f8f9fa', 
                justifyContent: 'flex-end',
                flexWrap: 'wrap', gap: 1
            }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {editMode && (
                        <IconButton 
                            color="error" 
                            onClick={handleDelete}
                            size="small"
                            title="Löschen"
                        >
                            <MdDelete />
                        </IconButton>
                    )}
                    <Button onClick={closeModal} color="inherit" size="small">Abbrechen</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSave} 
                        disabled={loading}
                        sx={{ px: { xs: 2, sm: 3 } }}
                        size="small"
                    >
                        {editMode ? "Speichern" : "Anlegen"}
                    </Button>
                </Box>
            </DialogActions>

            <ConfirmModal 
                open={confirmConfig.open}
                onClose={() => setConfirmConfig(prev => ({ ...prev, open: false }))}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
        </Dialog>
    );
};

export default MemberModal;
